import React from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { QUERY } from '../gql-operations';
import useOpenMessageReference from '../hooks/useOpenMessageReference';
import { getUrlMetaVideoSource, isUrlMetaVideo } from '../util/url-meta-media';

const normalizeReplyPreviewText = value =>
    String(value || '')
        .replace(/\s+/g, ' ')
        .trim();

const truncateReplyPreviewText = (value, maxLength = 88) => {
    const normalizedValue = normalizeReplyPreviewText(value);
    if (!normalizedValue) {
        return '';
    }

    if (normalizedValue.length <= maxLength) {
        return normalizedValue;
    }

    return `${normalizedValue.slice(0, maxLength - 1).trimEnd()}…`;
};

const resolveReplyPreviewThumbnail = previewMessage => {
    const image = previewMessage?.image;
    if (image?.thumbnail?.sm || image?.thumbnail?.md || image?.thumbnail?.lg || image?.cdn_url || image?.url) {
        return image.thumbnail?.sm || image.thumbnail?.md || image.thumbnail?.lg || image.cdn_url || image.url;
    }

    const file = previewMessage?.file;
    if (file?.thumbnail?.sm || file?.thumbnail?.md || file?.thumbnail?.lg) {
        return file.thumbnail?.sm || file.thumbnail?.md || file.thumbnail?.lg;
    }

    const firstUrlMeta = Array.isArray(previewMessage?.urlMeta) ? previewMessage.urlMeta[0] : null;
    if (
        firstUrlMeta?.thumbnail?.sm ||
        firstUrlMeta?.thumbnail?.md ||
        firstUrlMeta?.thumbnail?.lg ||
        firstUrlMeta?.image_cdn_url ||
        firstUrlMeta?.image ||
        getUrlMetaVideoSource(firstUrlMeta) ||
        firstUrlMeta?.author_image_thumbnail?.sm ||
        firstUrlMeta?.author_image_thumbnail?.md ||
        firstUrlMeta?.author_image_thumbnail?.lg ||
        firstUrlMeta?.author_image_cdn_url ||
        firstUrlMeta?.author_image_original ||
        firstUrlMeta?.author_image
    ) {
        return (
            firstUrlMeta.thumbnail?.sm ||
            firstUrlMeta.thumbnail?.md ||
            firstUrlMeta.thumbnail?.lg ||
            firstUrlMeta.image_cdn_url ||
            firstUrlMeta.image ||
            getUrlMetaVideoSource(firstUrlMeta) ||
            firstUrlMeta.author_image_thumbnail?.sm ||
            firstUrlMeta.author_image_thumbnail?.md ||
            firstUrlMeta.author_image_thumbnail?.lg ||
            firstUrlMeta.author_image_cdn_url ||
            firstUrlMeta.author_image_original ||
            firstUrlMeta.author_image
        );
    }

    return '';
};

const resolveReplyPreviewMediaLabel = (previewMessage, lnT) => {
    const image = previewMessage?.image;
    if (image?.url || image?.cdn_url || image?.thumbnail?.sm) {
        return lnT('common:photo');
    }

    const file = previewMessage?.file;
    const fileType = String(file?.type || file?.mime || '').toLowerCase();
    if (fileType.includes('image')) {
        return lnT('common:photo');
    }
    if (fileType.includes('video')) {
        return lnT('messages:video');
    }
    if (fileType.includes('audio')) {
        return lnT('folders:audio');
    }
    if (file?.url || file?.cdn_url) {
        return lnT('messages:file');
    }

    const firstUrlMeta = Array.isArray(previewMessage?.urlMeta) ? previewMessage.urlMeta[0] : null;
    if (isUrlMetaVideo(firstUrlMeta)) {
        return lnT('messages:video');
    }
    if (getUrlMetaVideoSource(firstUrlMeta) || firstUrlMeta?.image) {
        return lnT('messages:link');
    }

    return '';
};

const buildReplyPreviewContent = (previewMessage, lnT) => {
    if (!previewMessage) {
        return null;
    }

    const mediaLabel = resolveReplyPreviewMediaLabel(previewMessage, lnT);
    const primaryText = truncateReplyPreviewText(
        previewMessage?.title ||
            previewMessage?.body ||
            previewMessage?.file?.displayName ||
            previewMessage?.file?.name ||
            previewMessage?.image?.displayName ||
            previewMessage?.image?.name ||
            previewMessage?.urlMeta?.[0]?.title ||
            mediaLabel ||
            'Referenced message'
    );
    return {
        thumbnailUrl: resolveReplyPreviewThumbnail(previewMessage),
        label:
            previewMessage?.folder?.title ||
            (String(previewMessage?.aiRole || '').toUpperCase() === 'ASSISTANT' ? lnT('messages:aiReply') : lnT('messages:referencedMessage')),
        primaryText,
        secondaryText: ''
    };
};

export default function MessageReferencePreview({ message, onOpenMessage, onActivate }) {
    const { t: lnT } = useTranslation();
    const { openOriginalMessage, opening, targetId: referencedPreviewTargetId } =
        useOpenMessageReference(message, onOpenMessage);
    const { data, loading: replyPreviewLoading } = useQuery(QUERY.GET_MESSAGE_REPLY_PREVIEW, {
        variables: { data: { id: referencedPreviewTargetId } },
        skip: !/^[a-fA-F0-9]{24}$/.test(referencedPreviewTargetId),
        fetchPolicy: 'cache-first'
    });
    const replyPreview = data?.getMessage?.id === referencedPreviewTargetId
        ? buildReplyPreviewContent(data.getMessage, lnT)
        : null;

    const handleReplyPreviewClick = event => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof onActivate === 'function') {
            onActivate(event);
        } else {
            openOriginalMessage();
        }
    };

    if (!referencedPreviewTargetId) {
        return null;
    }

    const previewContent = replyPreviewLoading
        ? { thumbnailUrl: '', label: lnT('messages:referencedMessage'), primaryText: lnT('messages:loadingReferencedMessage') }
        : (replyPreview || { thumbnailUrl: '', label: lnT('messages:referencedMessage'), primaryText: lnT('messages:referencedMessageUnavailable') });
    const hasThumbnail = Boolean(previewContent.thumbnailUrl);
    const hasReferencePreview = Boolean(replyPreview) && !replyPreviewLoading;
    const isPreviewInteractive = typeof onActivate === 'function' || hasReferencePreview;
    return (
        <ReplyPreviewContainer
            type="button"
            data-message-interactive="true"
            disabled={!isPreviewInteractive || opening}
            onClick={isPreviewInteractive ? handleReplyPreviewClick : undefined}
            $isPlaceholder={!isPreviewInteractive}
        >
            <ReplyPreviewAccent />
            {hasThumbnail ? (
                <ReplyPreviewThumbnailWrap>
                    <ReplyPreviewThumbnail src={previewContent.thumbnailUrl} alt="" />
                </ReplyPreviewThumbnailWrap>
            ) : replyPreviewLoading ? (
                <ReplyPreviewThumbnailWrap>
                    <ReplyPreviewThumbnailPlaceholder />
                </ReplyPreviewThumbnailWrap>
            ) : null}
            <ReplyPreviewTextColumn>
                {hasReferencePreview ? (
                    <>
                        <ReplyPreviewLabel>{previewContent.label}</ReplyPreviewLabel>
                        <ReplyPreviewPrimaryText>{previewContent.primaryText}</ReplyPreviewPrimaryText>
                    </>
                ) : (
                    replyPreviewLoading ? (
                        <>
                            <ReplyPreviewLabel>
                                <ReplyPreviewSkeletonLine $width="108px" />
                            </ReplyPreviewLabel>
                            <ReplyPreviewPrimaryText>
                                <ReplyPreviewSkeletonLine $width="76%" />
                            </ReplyPreviewPrimaryText>
                        </>
                    ) : (
                        <>
                            <ReplyPreviewLabel>{previewContent.label}</ReplyPreviewLabel>
                            <ReplyPreviewPrimaryText>{previewContent.primaryText}</ReplyPreviewPrimaryText>
                        </>
                    )
                )}
            </ReplyPreviewTextColumn>
        </ReplyPreviewContainer>
    );
}

const ReplyPreviewContainer = styled.button`
    width: 100%;
    padding: 0;
    border: 0;
    font: inherit;
    text-align: left;
    display: flex;
    align-items: stretch;
    gap: 8px;
    min-width: 0;
    margin-bottom: 8px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.11);
    overflow: hidden;
    cursor: pointer;
    transition: background 140ms ease;

    &:hover {
        background: rgba(255, 255, 255, 0.16);
    }

    ${props =>
        props.$isPlaceholder &&
        css`
            cursor: default;

            &:hover {
                background: rgba(255, 255, 255, 0.11);
            }
        `}
`;

const ReplyPreviewAccent = styled.div`
    width: 4px;
    align-self: stretch;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.96);
`;

const ReplyPreviewThumbnailWrap = styled.div`
    padding: 8px 0;
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const ReplyPreviewThumbnail = styled.img`
    width: 54px;
    height: 54px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
    background: rgba(0, 0, 0, 0.16);
`;

const ReplyPreviewThumbnailPlaceholder = styled.div`
    width: 54px;
    height: 54px;
    border-radius: 8px;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.08);
`;

const ReplyPreviewTextColumn = styled.div`
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    padding: 8px 12px 8px 0;
`;

const ReplyPreviewLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    line-height: 1.25;
    color: rgba(255, 255, 255, 0.74);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${props =>
        props.$isPlaceholder &&
        css`
            color: rgba(255, 255, 255, 0.5);
        `}
`;

const ReplyPreviewPrimaryText = styled.div`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.98);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ReplyPreviewSkeletonLine = styled.div`
    width: ${props => props.$width || '100%'};
    max-width: 100%;
    height: 0.95em;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
`;
